import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const KeypadContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  padding: 1.5rem;
  min-width: 320px;
  max-width: 400px;
  width: 90%;
`;

const KeypadHeader = styled.div`
  text-align: center;
  margin-bottom: 0.75rem;
  font-size: 1rem;
  color: #666;
  font-weight: 500;
`;

const KeypadDisplay = styled.div`
  background: #2c3e50;
  color: #00ff00;
  font-size: 2rem;
  padding: 1rem;
  text-align: right;
  font-family: 'Courier New', monospace;
  margin-bottom: 1rem;
  border-radius: 6px;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  word-break: break-all;
`;

const KeypadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const KeypadButton = styled.button`
  padding: 1.25rem;
  font-size: 1.5rem;
  font-weight: 600;
  border: 1px solid #ddd;
  background: ${props => {
    if (props.$accent) return '#007bff';
    if (props.$danger) return '#dc3545';
    if (props.$success) return '#28a745';
    if (props.$special) return '#f8f9fa';
    return 'white';
  }};
  color: ${props => {
    if (props.$accent || props.$danger || props.$success) return 'white';
    return '#333';
  }};
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.1s;
  border-color: ${props => {
    if (props.$accent) return '#007bff';
    if (props.$danger) return '#dc3545';
    if (props.$success) return '#28a745';
    return '#ddd';
  }};

  &:hover {
    background: ${props => {
      if (props.$accent) return '#0056b3';
      if (props.$danger) return '#c82333';
      if (props.$success) return '#218838';
      if (props.$special) return '#e9ecef';
      return '#f0f0f0';
    }};
    transform: scale(0.98);
  }

  &:active {
    transform: scale(0.95);
  }

  ${props => props.$wide && `
    grid-column: span 2;
  `}
`;

const ActionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const CancelButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #5a6268;
  }
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #218838;
  }
`;

const NumericKeypad = ({
  isOpen,
  onClose,
  onConfirm,
  initialValue = '',
  title = 'Introducir valor',
  allowDecimals = true,
  formatAsCurrency = false,
  currencySymbol = '€'
}) => {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (isOpen) {
      setValue(initialValue?.toString() || '');
    }
  }, [isOpen, initialValue]);

  const handleKeyClick = useCallback((key) => {
    if (key === 'C') {
      setValue('');
    } else if (key === '←') {
      setValue(prev => prev.slice(0, -1));
    } else if (key === '.') {
      if (allowDecimals && !value.includes('.')) {
        setValue(prev => prev + '.');
      }
    } else {
      setValue(prev => prev + key);
    }
  }, [allowDecimals, value]);

  const handleConfirm = useCallback(() => {
    onConfirm(value);
    onClose();
  }, [value, onConfirm, onClose]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Handle keyboard input
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        handleKeyClick(e.key);
      } else if (e.key === '.' && allowDecimals) {
        handleKeyClick('.');
      } else if (e.key === 'Backspace') {
        handleKeyClick('←');
      } else if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter') {
        handleConfirm();
      } else if (e.key === 'c' || e.key === 'C') {
        handleKeyClick('C');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyClick, handleConfirm, onClose, allowDecimals]);

  if (!isOpen) return null;

  const displayValue = formatAsCurrency
    ? `${parseFloat(value || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencySymbol}`
    : value || '0';

  return (
    <Overlay onClick={handleOverlayClick}>
      <KeypadContainer onClick={(e) => e.stopPropagation()}>
        <KeypadHeader>{title}</KeypadHeader>
        <KeypadDisplay>{displayValue}</KeypadDisplay>

        <KeypadGrid>
          <KeypadButton onClick={() => handleKeyClick('7')}>7</KeypadButton>
          <KeypadButton onClick={() => handleKeyClick('8')}>8</KeypadButton>
          <KeypadButton onClick={() => handleKeyClick('9')}>9</KeypadButton>

          <KeypadButton onClick={() => handleKeyClick('4')}>4</KeypadButton>
          <KeypadButton onClick={() => handleKeyClick('5')}>5</KeypadButton>
          <KeypadButton onClick={() => handleKeyClick('6')}>6</KeypadButton>

          <KeypadButton onClick={() => handleKeyClick('1')}>1</KeypadButton>
          <KeypadButton onClick={() => handleKeyClick('2')}>2</KeypadButton>
          <KeypadButton onClick={() => handleKeyClick('3')}>3</KeypadButton>

          <KeypadButton onClick={() => handleKeyClick('0')} $wide>0</KeypadButton>
          {allowDecimals && (
            <KeypadButton onClick={() => handleKeyClick('.')}>.</KeypadButton>
          )}
          {!allowDecimals && (
            <KeypadButton onClick={() => handleKeyClick('←')} $special>←</KeypadButton>
          )}
        </KeypadGrid>

        <KeypadGrid style={{ gridTemplateColumns: '1fr 1fr' }}>
          <KeypadButton onClick={() => handleKeyClick('C')} $danger>C</KeypadButton>
          {allowDecimals && (
            <KeypadButton onClick={() => handleKeyClick('←')} $special>←</KeypadButton>
          )}
        </KeypadGrid>

        <ActionRow>
          <CancelButton onClick={onClose}>Cancelar</CancelButton>
          <ConfirmButton onClick={handleConfirm}>Aceptar</ConfirmButton>
        </ActionRow>
      </KeypadContainer>
    </Overlay>
  );
};

export default NumericKeypad;
